package com.yogaaicoach.pages.trainingprogram

import com.yogaaicoach.common.ui.components.*
import com.yogaaicoach.scope
import kotlinx.coroutines.launch
import react.ChildrenBuilder
import react.FC
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.span
import react.useEffectOnce
import react.useState
import web.cssom.ClassName

internal external interface AddNewTrainingScreen4PageProps : Props {
    var viewModel: AddNewTrainingViewModel
    var onNextButtonClicked: () -> Unit
}

private fun Weekday.shortTitle(): String = when (this) {
    Weekday.Monday -> "Mon"
    Weekday.Tuesday -> "Tue"
    Weekday.Wednesday -> "Wed"
    Weekday.Thursday -> "Thu"
    Weekday.Friday -> "Fri"
    Weekday.Saturday -> "Sat"
    Weekday.Sunday -> "Sun"
}

private fun Weekday.fullTitle(): String = name

private fun Duration.title(): String = "${minutes} min"

// Pixel-matched against AddNewTrainingScreen4.kt + WeekdayPillPicker.kt: a row of 7 day pills,
// then a duration picker (4 segmented buttons) for each checked day, with an error message above
// when submission validation fails. The "Create" button at the bottom replaces "Next" and shows
// a loading spinner while the create-training-program request is in flight.
internal val AddNewTrainingScreen4Page = FC<AddNewTrainingScreen4PageProps> { props ->
    var weekdays by useState(props.viewModel.weekdays.value)
    var errorText by useState(props.viewModel.scheduleErrorText.value)
    var isLoading by useState(props.viewModel.isLoading.value)

    useEffectOnce {
        scope.launch { props.viewModel.weekdays.collect { weekdays = it } }
        scope.launch { props.viewModel.scheduleErrorText.collect { errorText = it } }
        scope.launch { props.viewModel.isLoading.collect { isLoading = it } }
    }

    div {
        className = ClassName("wizard-page wizard-page-has-bottom-bar")

        StepProgressIndicator(4, listOf(CenterFocusStrongIcon, TrendingUpIcon, PersonIcon, CalendarMonthIcon))

        PageTitleSection(
            "Set Your Training Preferences",
            "This helps us create a personalized training plan that fits your schedule and keeps you on track to achieve your goals.",
            hint = "Pick the days and duration that feel right for you. You can adjust anytime."
        )

        div {
            className = ClassName("wizard-schedule")

            if (errorText != null) {
                p { className = ClassName("wizard-schedule-error"); +errorText!! }
            }

            WeekdayPillPicker(weekdays, props.viewModel::onToggleDayChecked, props.viewModel::updateSelectedDurationAdvanced)
        }

        div {
            className = ClassName("wizard-bottom-bar")
            button {
                className = ClassName(if (isLoading) "primary-button loading" else "primary-button")
                disabled = isLoading
                onClick = { props.viewModel.onSubmitSchedule(props.onNextButtonClicked) }
                if (isLoading) {
                    span { className = ClassName("button-spinner") }
                } else {
                    span { +"Create" }
                }
            }
        }
    }
}

private fun ChildrenBuilder.WeekdayPillPicker(
    weekdays: List<WeekdayState>,
    onDayToggled: (Weekday) -> Unit,
    onDurationUpdate: (Weekday, Duration) -> Unit,
) {
    div {
        className = ClassName("weekday-pill-row")
        weekdays.forEach { state ->
            button {
                className = ClassName(if (state.isChecked) "weekday-pill weekday-pill-checked" else "weekday-pill")
                onClick = { onDayToggled(state.weekday) }
                +state.weekday.shortTitle()
            }
        }
    }

    val selectedDays = weekdays.filter { it.isChecked }
    if (selectedDays.isNotEmpty()) {
        div { className = ClassName("weekday-duration-divider") }
        selectedDays.forEachIndexed { index, state ->
            DayDurationRow(state, onDurationUpdate)
            if (index < selectedDays.lastIndex) {
                div { className = ClassName("weekday-duration-divider") }
            }
        }
    }
}

private fun ChildrenBuilder.DayDurationRow(state: WeekdayState, onDurationUpdate: (Weekday, Duration) -> Unit) {
    div {
        className = ClassName("weekday-duration-row")
        p { className = ClassName("weekday-duration-row-title"); +state.weekday.fullTitle() }
        div {
            className = ClassName("duration-segmented-row")
            Duration.entries.forEachIndexed { index, duration ->
                button {
                    className = ClassName(
                        buildList {
                            add("duration-segment")
                            if (state.selectedDuration == duration) add("duration-segment-selected")
                            if (index == 0) add("duration-segment-first")
                            if (index == Duration.entries.lastIndex) add("duration-segment-last")
                        }.joinToString(" ")
                    )
                    onClick = { onDurationUpdate(state.weekday, duration) }
                    +duration.title()
                }
            }
        }
    }
}
